import React, { useState, useEffect, useRef } from 'react';
import {
  Form, FormGroup, Input, Button,Row, Col
} from 'reactstrap';
import LaddaButton, { EXPAND_RIGHT } from 'react-ladda';
import PropTypes from 'prop-types';

import { KYCConstants, getAccountType } from '../types';
import { CustomFormInput, FormField, SelectFormField } from '../../../../utils/FormFields';
import { CircularProgress } from '../../../..';
import { AccountMode } from '../../../../utils/Helper';
import { applyRepSync, resolveAdvisorSeed, rescopeAdvisor } from "../../../../utils/repAdvisor";
import ModalConfirm from "../../Modal/ModalConfirm";


const FinalizeTab = ({
  Finalize,
  accountId,
  account,
  setAccountData,
  setRedirectAccount,
  services,
  mode,
  userType,
  AutomaticRep = false,
  reps = [],
  newOnboardingVersion = false,
}) => {
  const [parameterResponse,setParameterResponse] = useState('');
  const [loadingSave, setLoadingSave] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [loadingCheck, setLoadingCheck] = useState(false);
  const [personlist, setPersonList] = useState([]);
  const [showResults, setShow] = useState(false);
  const [listResults, setResults] = useState([]);
  const [reportId, setReportid] = useState(0);
  const [repSyncing, setRepSyncing] = useState(false);
  const [syncedRep, setSyncedRep] = useState(null);
  const [modalConfirm, setModalConfirm] = useState({
    open: false,
    title: "",
    callback: undefined,
    loading: false,
    accountId: undefined
  });

  useEffect(() => {
    services.getParameterValuesByName("EXPORT_ACCOUNT_TO_INTEROP", (parameterResponse) => { 
      setParameterResponse(parameterResponse);
    })
  },[]);

  // Mount-time Rep synchronization.
  useEffect(() => {
    if (!AutomaticRep || !services || typeof services.fetchAccountRep !== "function") {
      return;
    }

    setRepSyncing(true);
    services.fetchAccountRep(accountId, (result) => {
      setRepSyncing(false);
      if (!result || !result.ok) {
        return;
      }
      setSyncedRep(result.data);
      const patch = applyRepSync(account, result.data);
      if (Object.keys(patch).length > 0) {
        setAccountData(patch);
      }
    });
  }, [accountId]);

  // Rep-derived advisor seeding and advisor re-scoping on Rep
  // change.
  //
  // A ref tracks the previously resolved RepId to distinguish two cases:
  //   - First resolution (no previous Rep): seed the advisor from the Rep's
  //     default via `resolveAdvisorSeed`, which only fills MainAdvisorId/Name when
  //     the advisor is currently unset and the Rep exposes a valid default;
  //     an existing non-zero advisor is preserved.
  //   - Resolved Rep changed: re-scope via `rescopeAdvisor`, which guarantees the
  //     resulting MainAdvisorId is empty or present in the new Rep's advisors list
  //     (kept if in-list, reseeded from the new Rep's default, else cleared) so no
  //     out-of-scope advisor persists.
  // Both helpers are pure and return a patch ({} means no change) merged via
  // `setAccountData`. The helpers are idempotent, so re-runs from a new `reps`
  // reference at the same RepId produce no change and cannot loop.
  const prevResolvedRepIdRef = useRef(undefined);
  useEffect(() => {
    if (!AutomaticRep) {
      return;
    }

    const resolvedRepId =
      account.RepId && Number(account.RepId) > 0 ? Number(account.RepId) : 0;

    if (!resolvedRepId) {
      prevResolvedRepIdRef.current = resolvedRepId;
      return;
    }

    const resolvedRep = (reps || []).find(
      (r) => Number(r.id) === resolvedRepId
    );

    if (!resolvedRep) {
      prevResolvedRepIdRef.current = resolvedRepId;
      return;
    }

    const previousResolvedRepId = prevResolvedRepIdRef.current;
    const patch =
      previousResolvedRepId !== undefined &&
      previousResolvedRepId !== resolvedRepId
        ? rescopeAdvisor(account, resolvedRep)
        : resolveAdvisorSeed(account, resolvedRep);

    if (Object.keys(patch).length > 0) {
      setAccountData(patch);
    }

    prevResolvedRepIdRef.current = resolvedRepId;
  }, [account.RepId, reps]);

  const handleCreateAccount = () => {
    setLoadingSave(true);

    // 1) Crear la cuenta
    let bodyCreate;
    let serviceToCall;
    if (newOnboardingVersion) {
      bodyCreate = { accountId: accountId };
      serviceToCall = services.fetchFinalizeOnboardingAccount;
    } else {
      bodyCreate = { onboardingProcessId: accountId };
      serviceToCall = services.createAccount;
    }
    
    serviceToCall(bodyCreate, (data) => {
      if (!data || !data.result) {
        alert("Unexpected error creating the account. Please contact an administrator.");
        setLoadingSave(false);
        return;
      }

      // 2) Si result.Code === 200, la cuenta se creó correctamente
      if (data.result.Code === 200) {
        alert(data.result.Description);

        const newAccountId = data.id;
        if (!newAccountId) {
          // Nunca debería pasar, si devuleve 200 es porque crea la cuenta, pero por las dudas
          alert("Unexpected error creating the account. Please contact an administrator.");
          setLoadingSave(false);
          return;
        }

        // 3) Leer parámetro de exportación
        if (typeof services.getParameterValuesByName !== "function") {
          // Error de código, no debería pasar
          alert("getParameterValuesByName service is not available, please contact administrator.");
          setLoadingSave(false);
          setRedirectAccount(newAccountId);
          return;
        }

        if (parameterResponse) {
          const exportAccountEnabled =
            parameterResponse &&
            parameterResponse.SdtParametersData &&
            parameterResponse.SdtParametersData.ParameterNumericValue == 1;
          
          const holders = Array.isArray(account.AccountInfoHolder) ? account.AccountInfoHolder : [];

          if (!exportAccountEnabled || !account.AccountIsPhysical || holders.length > 1) {
            // Parámetro desactivado, cuenta no es física, o tiene más de un holder, no tiene que exportar
            setLoadingSave(false);
            setRedirectAccount(newAccountId);
            return;
          }

          // 4) Parámetro activo, exportar
          if (typeof services.exportAccount !== "function") {
            // Error de código, no debería pasar
            alert("exportAccount service is not available, please contact administrator.");
            setLoadingSave(false);
            setRedirectAccount(newAccountId);
            return;
          }

          services.exportAccount(newAccountId).then((exportResponse) => {
            if (!exportResponse || !exportResponse.response || !exportResponse.response.result) {
              alert(
                "Account was created, but exporting data to Interop failed. Please contact administrator."
              );
              setLoadingSave(false);
              setRedirectAccount(newAccountId);
              return;
            }

            if (exportResponse.response.result.Code !== 200) {
              if (exportResponse.response.result.Code == 207) {
                // Main holder es mínima, no tiene que exportar
                setLoadingSave(false);
                setRedirectAccount(newAccountId);
                return;
              }
              
              alert(`There were errors exporting the account, please check account and person history for more information.`);
              setLoadingSave(false);
              setRedirectAccount(newAccountId);
              return;
            }

            alert("Account exported successfully.");
            setLoadingSave(false);
            setRedirectAccount(newAccountId);
            return;
          });
        }
      } else {
        // 5) result.Code !== 200: lógica de FundsX
        const description = data.result.Description || "";
        if (description.includes("FundsX")) {
          alert(description);
          setLoadingSave(false);
          setRedirectAccount(data.id);
        } else {
          alert(`Failed! ${description}`);
          setLoadingSave(false);
        }
      }
    });
  };

  
  // When AutomaticRep is active, persist the Profile data (Main Advisor and
  // commission collected in this tab) before creating the account, so the
  // created account reflects those values. Creation runs only inside the
  // persistence callback. When inactive, create directly as before.
  const confirmCreateAccount = () => {
    if (AutomaticRep) {
      services.fetchNewAccount(
        { account: { ...account }, accountChangeType: getAccountType.Profile },
        () => handleCreateAccount()
      );
    } else {
      handleCreateAccount();
    }
  };

  const createOnboardingAccount = () => {
    setModalConfirm(m => ({
      ...m,
      open: true,
      title: "Are you sure you want to send account information?",
      accountId,
      callback: () => confirmCreateAccount()
    }));
  };


  const listCheck = () => {
    setLoadingCheck(true)
    const body = {
     sdtApproveAccount: personlist,
     fromType: 'EasyAccount',
    }
    if (personlist.length < 1){
      alert("There aren't people to check!");
    }else{
    services.AccountListCheck(body, (data) => {
      if (data && data.error) {
        if (data.error.lenght > 0) {
          setResults(data.error);
          
        } 
        setReportid(data.logListId);
      }
      setLoadingCheck(false);
    });
  }
  }


  useEffect(() => {
    setLoadingData(true);
      const body = {
        AccountGetInfo: {
          GetAccountDataOnboardingId: accountId,
          GetAccountDataId: accountId,
          GetAccountDataType: getAccountType.Finalize,
        },
      };
      if (mode === AccountMode.New) {
        services.fetchNewAccountInfo(body, (data) => {
          if (data && data.SDTAccount) {
            setLoadingData(false);
            setPersonList(data.SDTAccount.SDTApproveAccount)
          }else{
            setLoadingData(false);
          }
        });
      }
      setLoadingData(false);
  }, []);

  const toggleModalConfirm = () =>
    setModalConfirm({
      open: false,
      title: "",
      accountId: undefined,
      callback: undefined,
      loading: false
    });

    const handleDownload = () => {
      services.getReportListCheck({
        logListId: reportId
      }).then(response => {
        window.open(response.response.url);
      });
    };
    // markAsFalsePositive(name, personId, marked) {
    //   var message = "";
    //   if (marked == false) {
    //     message =
    //       "Are you sure you want to mark person " + name + " as false positive?";
    //   } else {
    //     message =
    //       "Are you sure you want to unmark person " +
    //       name +
    //       " as false positive?";
    //   }
    //   this.setState({
    //     modalPersonId: personId,
    //     openModalConfirm: true,
    //     modalMessage: message,
    //     markModal: marked
    //   });
    // }
  

//   const handleSave = () => {
//     const Finalize = { ...Finalize };
//     const body = {
//       account: {
//         AccountId: accountId,
//         AccountOnboardingId : accountId,
//         KYC: newKYC,
//       },
//       accountChangeType: getAccountType.KYC,
//     };
//     setLoadingSave(true);
//     if (mode === AccountMode.New){
//       services.fetchNewAccount(body, (res) => {
//         if (res){
//           setAccountData({
//             AccountId: account.AccountOnboardingId,
//             KYC: newKYC,
//           }, true);
//           setChangedKYC({});
//           setLoadingSave(false);
//         }else{
//           setLoadingSave(false);
//         }
//       })
//     }else{
//     services.fetchAccountUpdate(body, ({ result }) => {
//       if (result && result.Code === 200) {
//         setAccountData({
//           AccountId: accountId,
//           KYC: newKYC,
//         }, true);
//         setChangedKYC({});
//         setLoadingSave(false);
//       } else {
//         setLoadingSave(false);
//       }
//     });
//   };
// }

  // const onChange = (name, value) => {
  //   if (!loadingSave) {
  //     const newKYC = { ...changedKYC };
  //     if (KYC[name] !== value) {
  //       newKYC[name] = value;
  //     } else if (newKYC[name]) {
  //       newKYC[name] = undefined;
  //     }
  //     setChangedKYC(newKYC);
  //   }
  // };

  //const revert = () => setChangedKYC({});


 if (loadingData) {
    return <CircularProgress sm />;
  }

  // Any Rep resolution/recomputation belongs upstream (in the getter/service), never here.
  const getRep = () => {
    const repId = account.RepId;
    return repId && Number(repId) > 0 ? Number(repId) : 0;
  };

  // Advisor options, scoped to the resolved Rep's advisors (mirrors ProfileForm).
  // Since AccountWizard seeds a default Rep under AutomaticRep, this normally
  // resolves to that Rep's advisors. In the rare case no Rep resolves (RepId = 0),
  // the dropdown shows only the empty option so MainAdvisorId stays 0 and the
  // backend fills DefaultMainAdvisorId.
  const resolvedRepId = getRep();
  const scopedRep = resolvedRepId
    ? (reps || []).filter((r) => Number(r.id) === resolvedRepId)
    : [];

  // Advisor source resolution, in precedence order:
  //   1. The resolved Rep is present in the Redux `reps` payload → use its
  //      advisors list (the normal path).
  //   2. The resolved Rep is NOT in `reps` (coupled getAccountRelatedEntities
  //      business logic can leave it absent) but the GetOnboardingAccountRep
  //      getter populated a non-zero MainAdvisorId/Name into account state →
  //      surface that single advisor as the option so both the Rep (shown via
  //      account.RepName) and its MainAdvisor render in the dropdown instead of
  //      collapsing to a blank selection.
  //   3. No Rep resolved → only the empty option.
  let advisorSource;
  if (scopedRep[0]) {
    advisorSource = scopedRep[0].advisors || [];
  } else if (
    resolvedRepId &&
    syncedRep &&
    Number(syncedRep.RepId) === resolvedRepId &&
    syncedRep.MainAdvisorId &&
    Number(syncedRep.MainAdvisorId) > 0
  ) {
    // The resolved Rep is absent from Redux `reps`, so its advisors list is not
    // available. Offer the getter's MainAdvisor as the sole option. This is
    // derived from the stored getter response (not the live selection), so the
    // option persists even after the operator picks the empty placeholder and
    // can therefore re-select the resolved advisor.
    advisorSource = [
      { id: syncedRep.MainAdvisorId, name: syncedRep.MainAdvisorName || "" },
    ];
  } else {
    advisorSource = [];
  }
  const advisorItems = [{ id: "", name: "" }].concat(advisorSource);
  // Only offer the dropdown when at least one real advisor option exists. When
  // none is available (Rep resolves to no advisors and the getter carried none),
  // the field is rendered as read-only text with the fallback message instead of
  // a dropdown whose message-bearing empty option cannot render once selected.
  const hasAdvisorOptions = advisorSource.length > 0;

  const onAdvisorChange = (name, value) => {
    if (name === "MainAdvisorId") {
      const selectedAdvisor = advisorItems.find(
        (advisor) => `${advisor.id}` === `${value}`
      );
      setAccountData({
        MainAdvisorId: value,
        MainAdvisorName: selectedAdvisor ? selectedAdvisor.name : "",
      });
    } else {
      setAccountData({ [name]: value });
    }
  };

  return (
    <div>
      <Row>
      {/* <Col md="7">
            <span style={{float: "left", fontSize: "18px"}}>Do you want to List check Account? </span>
      </Col>
      <Col style={{textAlign: "right"}}>
        <LaddaButton
              className="btn btn-ladda btn-primary"
              loading={loadingCheck}
              onClick={listCheck}
              data-style={EXPAND_RIGHT}
            >
             List Check
            </LaddaButton>
      </Col> */}
      <Col>
      {reportId > 0 &&
       <Button
       onClick={handleDownload}
       style={{marginRight: "10px"}}
     >
       {" "}
       <i className="fa fa-file-pdf-o" />
     </Button>
    }
     </Col>
     </Row>
     <Row className="my-3"> 
            <div>
              
            </div>
          </Row>
     {/* <Col md="7">
            <span style={{float: "left", fontSize: "18px"}}>Download Account Info </span>
      </Col> */}

      {/* <Col style={{textAlign: "right"}}>
       <Button
       onClick={handleDownload}
       style={{marginRight: "220px"}}
     >
       {" "}
       <i className="fa fa-file-pdf-o" />
     </Button>
     </Col> */}
     <Row>


     </Row>

<Row xl="4">
     
      {showResults && 
      <Table>
      <thead>
        <tr>
          <th>Person Name</th>
          <th>Person Checked Name</th>
          <th>List Name</th>
          <th>Person Relation</th>
          <th style={{textAlign: "right"}}>Marked as false positive</th>
        </tr>
      </thead>
      <tbody>
        {listResults.map((r, i) => (
          <tr key={`set-${r.id}`}>
            <td>{r.personName}</td>
            <td>{r.personCheckName}</td>
            <td>{r.listName}</td>
            <td>{r.personRelation}</td>
            <td>
              {r.belongPersonList == true ? (
                <Button
                  style={{float: "right"}}
                  color="primary"
                  outline
                  onClick={() =>
                    markAsFalsePositive(r.personName, r.id, false)
                  }
                >
                  Unmark as false positive
                </Button>
              ) : (
                <Button
                  style={{float: "right"}}
                  color="primary"
                  outline
                  onClick={() =>
                    markAsFalsePositive(r.personName, r.id, true)
                  }
                >
                  Mark as false positive
                </Button>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </Table>
    }
    </Row>
      {AutomaticRep && (
        <Row className="my-3">
          <Col md="4">
            {/* Rep is read-only here: under the AutomaticRep flow it is not
                selected manually. AccountWizard seeds it with the backend
                DefaultRepId at process start, so RepName normally shows that
                default Rep. The People-based auto-calculation is not implemented
                yet (out of scope); the fallback text covers the case where no
                Rep/name has resolved. */}
            <CustomFormInput label="Rep: ">
              {repSyncing
                ? <CircularProgress sm />
                : (account.RepName ? account.RepName : "Default Rep will be assigned automatically")}
            </CustomFormInput>
          </Col>
          <Col md="4">
            {repSyncing ? (
              <CustomFormInput label="Main Advisor: ">
                <CircularProgress sm />
              </CustomFormInput>
            ) : hasAdvisorOptions ? (
              <SelectFormField
                label="Main Advisor: "
                name="MainAdvisorId"
                value={account.MainAdvisorId}
                selectItems={advisorItems}
                getSelectItemId={(i) => i.id}
                getSelectItemName={(i) => i.name}
                onChange={onAdvisorChange}
              />
            ) : (
              <CustomFormInput label="Main Advisor: ">
                Default advisor of the assigned Rep will be used
              </CustomFormInput>
            )}
          </Col>
          <Col md="4">
            <FormField
              label="Main Advisor Commission %: "
              value={account.MainAdvisorCommissionPercentage}
              name="MainAdvisorCommissionPercentage"
              onChange={onAdvisorChange}
              type="number"
              dataTypeDecimalName="percentage"
            />
          </Col>
        </Row>
      )}
      <Row style={{textAlign: "right"}}> 
        {/* {userType === 'Compliance' && */}
        
    
        <Col style={{textAlign: "right"}}>
        <LaddaButton
              className="btn btn-ladda btn-primary"
              loading={loadingSave}
              onClick={createOnboardingAccount}
              data-style={EXPAND_RIGHT}
            >
              Create Account
            </LaddaButton>
        </Col>
      </Row>
      <ModalConfirm
        open={modalConfirm.open}
        toggle={toggleModalConfirm}
        title={modalConfirm.title}
        callback={modalConfirm.callback}
        saving={loadingSave}
      />
    </div>
  );
};

FinalizeTab.propTypes = {
  accountId: PropTypes.string.isRequired,
  setAccountData: PropTypes.func.isRequired,
  services: PropTypes.object.isRequired,
  account: PropTypes.object.isRequired,
  mode: PropTypes.object.isRequired,
  AutomaticRep: PropTypes.bool,
  reps: PropTypes.array,
};

FinalizeTab.defaultProps = {
  Finalize: undefined,
  AutomaticRep: false,
  reps: [],
};

export default FinalizeTab;
